import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ScanLine, Inbox, MessageSquare, FileBox, ShieldCheck, ArrowRight, CheckCircle2, Zap } from "lucide-react";
import { BrandFilm } from "@/components/BrandFilm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SCANNERS = [
    { name: "3Shape", sub: "TRIOS · Communicate" },
    { name: "DS Core", sub: "Dentsply Sirona · Primescan" },
    { name: "Medit", sub: "Medit Link · i-series" },
    { name: "Shining 3D", sub: "Aoralscan · cloud" },
];

const INBOX_ROWS = [
    { practice: "Riverside Dental Practice", via: "3Shape API" },
    { practice: "Dr. Emily Carter", via: "Medit API" },
    { practice: "City Dental Group", via: "DS Core API" },
    { practice: "Oakwood Dental Clinic", via: "Portal upload" },
    { practice: "Dr. James Miller", via: "Shining 3D API" },
];

function Bullet({ children }) {
    return (
        <li className="flex items-start gap-3">
            <span className="text-cyan-600 mt-1 shrink-0">&raquo;</span>
            <span className="text-[15px] text-slate-700 font-medium">{children}</span>
        </li>
    );
}

export default function DentalPractice() {
    const [testimonials, setTestimonials] = useState([]);
    useEffect(() => {
        axios.get(`${API}/practice-testimonials`)
            .then(({ data }) => setTestimonials(Array.isArray(data) ? data : []))
            .catch(() => {});
    }, []);
    return (
        <div className="bg-white" data-testid="solutions-dental-practice-page">
            {/* Hero */}
            <section className="max-w-6xl mx-auto px-6 pt-20 pb-16 grid lg:grid-cols-2 gap-14 items-center">
                <div>
                    <div className="text-[11px] font-mono-tr tracking-[0.28em] text-cyan-600 uppercase mb-5">
                        Toothmatrix Practice · Dental Practice Management
                    </div>
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light text-slate-900 leading-[1.05]">
                        One inbox<br />for your <em className="not-italic text-cyan-600">scans.</em>
                    </h1>
                    <p className="mt-6 text-base text-slate-600 max-w-md leading-relaxed">
                        Whatever the scanner — every case in one place. No more switching between
                        intraoral scanner portals, WhatsApp and WeTransfer. Your orders arrive in
                        one clean inbox, tracked from prep to delivery.
                    </p>
                    <ul className="mt-6 space-y-2.5">
                        <Bullet>3Shape, DS Core, Medit &amp; Shining 3D — directly integrated</Bullet>
                        <Bullet>STL, DICOM &amp; more — via your own practice portal</Bullet>
                        <Bullet>No portal hopping, no expiring links</Bullet>
                    </ul>
                    <div className="mt-9 flex flex-wrap gap-3">
                        <Link
                            to="/practice/register"
                            data-testid="dp-register-cta"
                            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-slate-900 hover:bg-slate-700 text-white text-[14px] font-medium transition-colors"
                        >
                            Register your practice <ArrowRight size={15} />
                        </Link>
                        <a
                            href="#how-it-works"
                            data-testid="dp-how-cta"
                            className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-slate-300 hover:border-slate-500 text-slate-800 text-[14px] font-medium transition-colors"
                        >
                            See how it works
                        </a>
                    </div>
                </div>

                {/* Inbox mock — clean light illustration (theme-independent: arbitrary
                    color classes escape the global html.dark overrides) */}
                <div className="relative py-2" data-testid="dp-inbox-mock">
                    {/* Dashed connectors behind the cards */}
                    <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 460 560" preserveAspectRatio="none" aria-hidden>
                        <path d="M 350 78 C 420 130, 400 180, 330 214" fill="none" stroke="#7DD3FC" strokeWidth="1.6" strokeDasharray="5 6" />
                        <path d="M 96 140 C 40 210, 40 300, 120 360" fill="none" stroke="#93C5FD" strokeWidth="1.6" strokeDasharray="5 6" />
                        <path d="M 330 262 C 400 320, 420 400, 348 452" fill="none" stroke="#86EFAC" strokeWidth="1.6" strokeDasharray="5 6" />
                        <circle cx="404" cy="128" r="5" fill="#38BDF8" />
                        <circle cx="52" cy="248" r="5" fill="#1D4ED8" />
                        <circle cx="404" cy="384" r="5" fill="#4ADE80" />
                    </svg>

                    {/* Card 1 — new scans */}
                    <div className="relative rounded-2xl border-[1.5px] border-[#38BDF8] bg-[#ffffff] shadow-[0_10px_30px_-12px_rgba(2,132,199,0.25)] p-4 w-fit ml-auto mr-6">
                        <span className="absolute top-1/2 -translate-y-1/2 -right-1.5 h-3 w-3 rounded-full bg-[#38BDF8]" />
                        <div className="flex items-center gap-2 text-[14px] font-semibold text-[#0f172a]">
                            <ScanLine size={16} className="text-[#0284C7]" /> New scans received
                        </div>
                        <div className="flex gap-2 mt-2.5">
                            {["3Shape", "DS Core", "Medit"].map((s) => (
                                <span key={s} className="px-2.5 py-1 rounded-md bg-[#E0F2FE] text-[#0369A1] text-[12px] font-medium">{s}</span>
                            ))}
                        </div>
                    </div>

                    {/* Card 2 — new message */}
                    <div className="relative rounded-2xl border-[1.5px] border-[#1D4ED8] bg-[#ffffff] shadow-[0_10px_30px_-12px_rgba(29,78,216,0.22)] p-4 w-fit mt-5">
                        <span className="absolute top-1/2 -translate-y-1/2 -right-1.5 h-3 w-3 rounded-full bg-[#1D4ED8]" />
                        <div className="flex items-center gap-2 text-[14px] font-semibold text-[#0f172a]">
                            <MessageSquare size={15} className="text-[#1D4ED8]" /> New message
                        </div>
                        <div className="text-[13px] text-[#475569] mt-1.5">Dr. Carter · Crown 36 · #32424</div>
                    </div>

                    {/* Card 3 — order due */}
                    <div className="relative rounded-2xl border-[1.5px] border-[#22C55E] bg-[#ffffff] shadow-[0_10px_30px_-12px_rgba(34,197,94,0.25)] p-4 w-fit mt-5 ml-24">
                        <span className="absolute top-1/2 -translate-y-1/2 -right-1.5 h-3 w-3 rounded-full bg-[#22C55E]" />
                        <div className="flex items-center gap-2 text-[14px] font-semibold text-[#0f172a]">
                            <CheckCircle2 size={15} className="text-[#16A34A]" /> Order due
                        </div>
                        <div className="text-[13px] text-[#475569] mt-1.5">#68916 · tomorrow, 1:00 p.m.</div>
                    </div>

                    {/* Inbox table */}
                    <div className="relative rounded-[22px] border border-[#E2E8F0] bg-[#ffffff] shadow-[0_28px_60px_-24px_rgba(15,23,42,0.25)] overflow-hidden mt-6 max-w-md mx-auto">
                        <div className="flex items-center gap-2 px-6 py-4 border-b border-[#F1F5F9] bg-[#ffffff]">
                            <Inbox size={16} className="text-[#0f172a]" />
                            <span className="text-[15px] font-semibold text-[#0f172a]">Inbox</span>
                            <span className="w-5 h-5 rounded-full bg-[#0284C7] text-[#ffffff] text-[11px] flex items-center justify-center font-semibold">5</span>
                        </div>
                        <div className="grid grid-cols-[1.5fr_1fr] px-6 py-2.5 text-[11px] font-semibold tracking-[0.14em] text-[#94A3B8] uppercase border-b border-[#F1F5F9] bg-[#F8FAFC]">
                            <span>Practice</span><span>Via</span>
                        </div>
                        {INBOX_ROWS.map((r, i) => (
                            <div key={r.practice} className={`grid grid-cols-[1.5fr_1fr] px-6 py-3.5 text-[13.5px] border-b border-[#F8FAFC] last:border-0 ${i === 0 ? "bg-[#EFF6FF]" : "bg-[#ffffff]"}`}>
                                <span className="text-[#334155]">{r.practice}</span>
                                <span className="font-semibold text-[#0f172a]">{r.via}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Scanner strip */}
            <section className="border-y border-slate-100 bg-slate-50/60">
                <div className="max-w-6xl mx-auto px-6 py-10 grid grid-cols-2 md:grid-cols-4 gap-6">
                    {SCANNERS.map((s) => (
                        <div key={s.name} className="text-center">
                            <div className="text-[17px] font-semibold text-slate-900">{s.name}</div>
                            <div className="text-[12px] text-slate-500 mt-1">{s.sub}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* One source of truth */}
            <section id="how-it-works" className="max-w-6xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-14 items-center">
                <div>
                    <h2 className="text-lg md:text-lg font-semibold text-slate-900 sm:text-3xl sm:font-light">One source of truth per order.</h2>
                    <p className="mt-4 text-[15px] text-slate-600 leading-relaxed max-w-md">
                        Order information scatters across channels, media and heads — the Scan
                        Inbox pulls it together on the order itself.
                    </p>
                    <ul className="mt-6 space-y-2.5">
                        <Bullet>Details, deadlines, tasks, chat and files — right on the order</Bullet>
                        <Bullet>One up-to-date source the whole team can rely on</Bullet>
                        <Bullet>No digging through inboxes, chats and memory</Bullet>
                    </ul>
                </div>
                <div className="rounded-2xl bg-cyan-600 p-6 lg:p-9">
                    <div className="rounded-xl bg-white shadow p-4 mb-4">
                        <div className="text-[14px] font-semibold text-slate-900">Michael Bennett · Crown 36 · Dr. Carter</div>
                        <div className="grid grid-cols-2 gap-3 mt-3 text-[12.5px]">
                            <div className="rounded-lg border border-slate-200 p-3">
                                <div className="font-mono-tr text-[10px] tracking-[0.18em] text-slate-400 uppercase mb-1.5">Status</div>
                                <span className="px-2 py-0.5 rounded bg-cyan-600 text-white text-[11px] font-medium">In progress</span>
                            </div>
                            <div className="rounded-lg border border-slate-200 p-3">
                                <div className="font-mono-tr text-[10px] tracking-[0.18em] text-slate-400 uppercase mb-1.5">Ship until</div>
                                <span className="text-slate-800 font-medium">20 July, 13:00</span>
                            </div>
                            <div className="rounded-lg border border-slate-200 p-3">
                                <div className="font-mono-tr text-[10px] tracking-[0.18em] text-slate-400 uppercase mb-1.5">Material</div>
                                <span className="text-slate-800 font-medium">Zirconia</span>
                            </div>
                            <div className="rounded-lg border border-slate-200 p-3">
                                <div className="font-mono-tr text-[10px] tracking-[0.18em] text-slate-400 uppercase mb-1.5">Scans &amp; files</div>
                                <span className="text-slate-800 font-medium">upper_jaw_scan.stl</span>
                            </div>
                        </div>
                        <div className="mt-3 text-[12px] text-slate-500 border-t border-slate-100 pt-2.5">
                            Practice note: &ldquo;Patient grinds&rdquo;
                        </div>
                    </div>
                    <div className="rounded-xl bg-white/95 p-4">
                        <div className="text-[12px] font-mono-tr tracking-[0.16em] text-slate-400 uppercase mb-2">Messages</div>
                        <div className="text-[13px] text-slate-700"><strong>Dr. Carter:</strong> Thanks for the heads-up! The opposing tooth will be crowned as well.</div>
                    </div>
                </div>
            </section>

            {/* Every question on the right case */}
            <section className="bg-slate-900">
                <div className="max-w-6xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-14 items-center">
                    <div className="rounded-2xl bg-slate-800/70 border border-slate-700 p-6 order-2 lg:order-1">
                        <div className="rounded-lg bg-slate-700/60 p-3.5 mb-3 max-w-sm">
                            <div className="text-[12px] text-cyan-300 mb-1">Tom Fisher · Lab</div>
                            <div className="text-[13.5px] text-slate-200">The distobuccal cusp on 26 is strongly elongated. Is an occlusal plane adjustment planned?</div>
                        </div>
                        <div className="rounded-lg bg-cyan-600/25 border border-cyan-500/30 p-3.5 ml-10 max-w-sm">
                            <div className="text-[12px] text-cyan-300 mb-1">Dr. Carter</div>
                            <div className="text-[13.5px] text-slate-200">Thanks for the heads-up! The 26 will be crowned in the coming months. The cusp can be reduced.</div>
                        </div>
                    </div>
                    <div className="order-1 lg:order-2">
                        <h2 className="text-lg md:text-lg font-semibold text-white sm:text-3xl sm:font-light">Every question on the right case.</h2>
                        <p className="mt-4 text-[15px] text-slate-400 leading-relaxed max-w-md">
                            A question about a crown, a photo of the prep, a quick voice note — today
                            it&apos;s spread over WhatsApp, WeTransfer and phone calls. Here, it happens on the case.
                        </p>
                        <ul className="mt-6 space-y-2.5">
                            <li className="flex items-start gap-3"><span className="text-cyan-400 mt-1">&raquo;</span><span className="text-[15px] text-slate-200 font-medium">Chat with the lab — right on the order</span></li>
                            <li className="flex items-start gap-3"><span className="text-cyan-400 mt-1">&raquo;</span><span className="text-[15px] text-slate-200 font-medium">Files and photos attached to the case, not your phone</span></li>
                            <li className="flex items-start gap-3"><span className="text-cyan-400 mt-1">&raquo;</span><span className="text-[15px] text-slate-200 font-medium">Nothing gets lost, nothing needs forwarding</span></li>
                        </ul>
                    </div>
                </div>
            </section>

            {/* BRAND FILM — every visitor path sees the film */}
            <section className="max-w-6xl mx-auto px-6 pt-16 -mb-2" data-testid="dp-brand-film-section">
                <div className="relative aspect-video rounded-3xl overflow-hidden border border-slate-200 shadow-[0_30px_80px_-30px_rgba(8,47,73,0.35)]">
                    <BrandFilm />
                </div>
            </section>

            {/* Pricing — free forever */}
            <section id="pricing" className="bg-slate-50 border-y border-slate-200 py-20" data-testid="dp-pricing">
                <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-start">
                    <div>
                        <div className="text-[11px] font-mono-tr tracking-[0.28em] text-cyan-600 uppercase mb-4">Pricing for practices</div>
                        <h2 className="text-lg md:text-lg font-semibold sm:text-4xl sm:font-light text-slate-900">Free. Forever.</h2>
                        <p className="mt-4 text-[15px] text-slate-600 max-w-md leading-relaxed">
                            Toothmatrix Practice costs your clinic nothing. The platform is covered by
                            your laboratory&rsquo;s Toothmatrix Lab subscription — you just scan and send.
                        </p>
                        <div className="mt-8 rounded-3xl border-2 border-cyan-500 bg-white p-8 max-w-md shadow-lg shadow-cyan-100/60" data-testid="dp-free-card">
                            <div className="flex items-baseline gap-2">
                                <span className="text-5xl font-light text-slate-900">$0</span>
                                <span className="text-slate-500 text-[14px]">/ forever</span>
                            </div>
                            <ul className="mt-6 space-y-2.5">
                                {[
                                    "Unlimited scan orders & team members",
                                    "All scanner integrations — 3Shape · DS Core · Medit · Shining 3D",
                                    "Live order tracking + direct lab chat",
                                    "STL · DICOM · PLY uploads up to 100 MB per file",
                                    "No credit card. No trial clock. No storage caps.",
                                ].map((f) => (
                                    <li key={f} className="flex items-start gap-2.5 text-[13.5px] text-slate-700">
                                        <CheckCircle2 size={15} className="text-emerald-600 mt-0.5 shrink-0" /> {f}
                                    </li>
                                ))}
                            </ul>
                            <Link to="/practice/register" data-testid="dp-pricing-cta"
                                className="mt-7 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-cyan-600 hover:bg-cyan-500 text-white text-[13.5px] font-semibold transition-colors">
                                Register your practice — free <ArrowRight size={14} />
                            </Link>
                            <a href={`${API}/practice-flyer.pdf`} target="_blank" rel="noreferrer" data-testid="dp-flyer-link"
                                className="mt-3 w-full inline-flex items-center justify-center gap-1.5 text-[12.5px] text-slate-500 hover:text-cyan-700 transition-colors">
                                <FileBox size={13} /> Print the front-desk flyer (1-page PDF)
                            </a>
                        </div>
                    </div>
                    <div data-testid="dp-faq">
                        <div className="text-[11px] font-mono-tr tracking-[0.28em] text-cyan-600 uppercase mb-4">Common questions</div>
                        <div className="space-y-4">
                            {[
                                { id: "really-free", q: "Is it really free?", a: "Yes — free for dental clinics, forever. No trial windows, no per-user fees, no feature gates. If you treat patients and send work to a lab, you never pay for Toothmatrix Practice." },
                                { id: "who-pays", q: "Who pays for the platform?", a: "Your dental laboratory. Labs run on Toothmatrix Lab (the paid product) and the practice portal is included in their subscription — the same way your lab covers its own phone line." },
                                { id: "scanners", q: "Which intraoral scanners are supported?", a: "3Shape (TRIOS), DS Core (Primescan / Dentsply Sirona), Medit (i-series) and Shining 3D (Aoralscan) via integrations — plus manual STL, DICOM, PLY or OBJ upload from any other system." },
                                { id: "data", q: "Who owns our data?", a: "Your practice does. Scans and case data are isolated per practice, stored securely, and you can request export or deletion at any time." },
                                { id: "start", q: "How do we get started?", a: "Register your practice in two minutes. The lab reviews and approves it, then emails your private portal link — usually within one business day. No installation, works in any browser." },
                            ].map((item) => (
                                <details key={item.id} className="group bg-white rounded-xl border border-slate-200 p-5 open:border-cyan-300" data-testid={`dp-faq-${item.id}`}>
                                    <summary className="cursor-pointer list-none flex items-center justify-between text-[15px] font-medium text-slate-900">
                                        {item.q}
                                        <span className="text-slate-400 group-open:rotate-45 transition-transform">+</span>
                                    </summary>
                                    <p className="mt-3 text-[13.5px] text-slate-600 leading-relaxed">{item.a}</p>
                                </details>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Testimonials — only rendered once the lab has published clinic quotes */}
            {testimonials.length > 0 && (
                <section className="max-w-6xl mx-auto px-6 py-20" data-testid="dp-testimonials">
                    <div className="text-[11px] font-mono-tr tracking-[0.28em] text-cyan-600 uppercase mb-4">Clinics on Toothmatrix Practice</div>
                    <h2 className="text-lg md:text-lg font-semibold sm:text-4xl sm:font-light text-slate-900 mb-10">What practices say.</h2>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {testimonials.map((t) => (
                            <figure key={t.id} className="rounded-2xl border border-slate-200 bg-white p-6" data-testid={`dp-quote-${t.id}`}>
                                <blockquote className="text-[14.5px] text-slate-700 leading-relaxed">&ldquo;{t.quote}&rdquo;</blockquote>
                                <figcaption className="mt-4 text-[12.5px] text-slate-500">
                                    <span className="font-semibold text-slate-900">{t.author || t.practice_name}</span>
                                    {t.author && t.practice_name && <> · {t.practice_name}</>}
                                    {t.city && <>, {t.city}</>}
                                </figcaption>
                            </figure>
                        ))}
                    </div>
                </section>
            )}

            {/* Trust + CTA */}
            <section className="max-w-6xl mx-auto px-6 py-20">
                <div className="grid md:grid-cols-3 gap-6 mb-16">
                    <div className="rounded-2xl border border-slate-200 p-6">
                        <Zap size={18} className="text-cyan-600 mb-3" />
                        <div className="text-[15px] font-semibold text-slate-900 mb-1.5">Pick your scanner — or let it autodetect</div>
                        <p className="text-[13.5px] text-slate-600 leading-relaxed">Choose 3Shape, DS Core, Medit or Shining 3D at registration. Incoming scans route to the lab automatically.</p>
                    </div>
                    <div className="rounded-2xl border border-slate-200 p-6">
                        <FileBox size={18} className="text-cyan-600 mb-3" />
                        <div className="text-[15px] font-semibold text-slate-900 mb-1.5">STL · DICOM · PLY · OBJ</div>
                        <p className="text-[13.5px] text-slate-600 leading-relaxed">Drag files straight into your portal — up to 100&nbsp;MB per file, stored securely, never behind an expiring link.</p>
                    </div>
                    <div className="rounded-2xl border border-slate-200 p-6">
                        <ShieldCheck size={18} className="text-cyan-600 mb-3" />
                        <div className="text-[15px] font-semibold text-slate-900 mb-1.5">Built by a working lab</div>
                        <p className="text-[13.5px] text-slate-600 leading-relaxed">Designed on a real production floor in Doha — every order lands in the same kanban your technicians already use.</p>
                    </div>
                </div>
                <div className="rounded-3xl bg-slate-900 px-8 py-14 text-center">
                    <h2 className="text-lg md:text-lg font-semibold text-white sm:text-3xl sm:font-light">Dentistry is hard enough.<br className="hidden sm:block" /> Sending scans to your lab shouldn&apos;t be.</h2>
                    <Link
                        to="/practice/register"
                        data-testid="dp-bottom-cta"
                        className="inline-flex items-center gap-2 mt-8 px-7 py-3 rounded-full bg-cyan-500 hover:bg-cyan-400 text-slate-900 text-[14px] font-semibold transition-colors"
                    >
                        Register your practice — free <ArrowRight size={15} />
                    </Link>
                </div>
                {/* Cross-sell: the sibling product for laboratories */}
                <div className="mt-6 rounded-2xl border border-slate-200 px-6 py-4 flex flex-wrap items-center justify-between gap-3">
                    <p className="text-[13.5px] text-slate-600">
                        <span className="font-semibold text-slate-900">Run a dental laboratory?</span>{" "}
                        Toothmatrix Lab is the full lab operating system — cases, Kanban, invoicing, payroll.
                    </p>
                    <Link to="/solutions/dental-labs" data-testid="dp-crosslink-lab"
                        className="inline-flex items-center gap-1.5 text-[13px] font-medium text-cyan-700 hover:text-cyan-500">
                        Explore Toothmatrix Lab <ArrowRight size={13} />
                    </Link>
                </div>
            </section>
        </div>
    );
}
